import React from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DeviceMockup,
  MonitorFrame,
  MonitorScreen,
  MonitorImage,
  CenterContent,
  MainTitle,
  WorkSpaceText,
  Subtitle,
  StatusBadge,
  Divider,
  Description,
  ServiceLink,
  ServiceTabs,
  ServiceTabsWrapper,
  ServiceTab,
  ServiceTabGlassOverlay,
  ServiceTabRing,
  ServiceIconContainer,
  ServiceTabImage,
  LeftNavIcon,
  RightNavIcon,
  ServiceCard,
  ServicesContent,
} from './ServicesStyles';

// mainTitle の後ろに添える小さい方の語。空文字を指定したサービスは
// 「大きい語だけ」で見せる（語そのものが長い場合、従来どおり
// 添え語を出すと 35% 幅のカラムで折り返してバッジと重なるため）。
// キー自体が無いサービスは従来どおり 'WORK SPACE' にフォールバックする。
const resolveWorkspaceText = (service) => service.workspaceText ?? 'WORK SPACE';

// mainTitle は「EC」「AI」のような 2〜3 文字を想定した 72px 見出し。PAWAPO のように
// 語が長いサービスは titleScale（em 比）で詰める。em なのでブレークポイントごとの
// fontSize にそのまま追従する（px で上書きするとレスポンシブが死ぬ）。
const mainTitleStyle = (service) => ({
  fontStyle: 'italic',
  ...(service.titleScale ? { fontSize: `${service.titleScale}em` } : {}),
});

// StatusBadge は既定ではカラム右端への absolute 配置。タイトルの長さと無関係に
// 置かれるので、PAWAPO のように語が長いとイタリック体の右上がバッジに食い込む。
// badgeInline のサービスは通常フローの flex 要素にして、構造的に重ならないようにする
// （既定の absolute のままにしてある他サービスの見た目は変わらない）。
const badgeSx = (service) =>
  service.badgeInline
    ? { position: 'static', alignSelf: 'center', flexShrink: 0, ml: '6px' }
    : undefined;


// 自社サービスなど外部サイトがあるものは Description の下にリンクを出す。
// 被リンクとして機能させたいので rel は noopener のみ（nofollow は付けない）。
const ServiceLinkComponent = ({ service }) =>
  service.link ? (
    <ServiceLink href={service.link} target="_blank" rel="noopener">
      {service.linkLabel || service.link}
      <span aria-hidden="true">↗</span>
    </ServiceLink>
  ) : null;

// mockup を外部サイトへのリンクで包む（link が無いサービスはそのまま）。
// noreferrer は付けない（makenarrative.com 側のアクセス解析で参照元を見たいため。
// noopener だけでも window.opener は遮断される）。
const wrapWithLink = (service, children) =>
  service.link ? (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a href={service.link} target="_blank" rel="noopener" aria-label={service.title}
       style={{ display: 'block' }}>
      {children}
    </a>
  ) : children;

export const DeviceMockupComponent = ({ currentService }) => {
  // mockupプロパティがあればそれを使用、なければimageを使用
  const imageSrc = currentService.mockup || currentService.image;
  const hasMockup = !!currentService.mockup;
  
  return (
    <DeviceMockup>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentService.title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {wrapWithLink(currentService, (
            <MonitorFrame hasMockup={hasMockup}>
              <MonitorScreen gradient={currentService.gradientColors.from} hasMockup={hasMockup}>
                <MonitorImage 
                  src={imageSrc} 
                  alt={currentService.title}
                  hasMockup={hasMockup}
                />
              </MonitorScreen>
            </MonitorFrame>
          ))}
        </motion.div>
      </AnimatePresence>
    </DeviceMockup>
  );
};

export const ServiceTabsComponent = ({ services, selected, onSelect }) => {
  // アイコンは servicesData の tabIcon を使う（配列の並び順に依存させない）。
  // 以前はここに tab_item_1..4 をハードコードしていたため、サービスを
  // 増減すると別サービスのアイコンが出てしまっていた。

  // 半円状に配置するための計算
  // 4つのタブを180度の半円に配置（右側から左側へ）
  const totalTabs = services.length;
  const startAngle = -90; // 上から開始（-90度）
  const endAngle = 90; // 下まで（90度）
  const angleStep = (endAngle - startAngle) / (totalTabs - 1);
  const radius = 180; // 円の半径（px）- 小さくして画面内に収める

  return (
    <ServiceTabsWrapper>
      <ServiceTabs>
        {services.map((service, i) => {
          const isSelected = selected === i;
          // 各タブの角度を計算
          const angle = startAngle + angleStep * i;
          // 角度をラジアンに変換
          const angleRad = (angle * Math.PI) / 180;
          // 円の中心点からの位置を計算（右側中央を基準）
          const centerX = 100; // 右側からの距離（px）- 少し左に移動
          const centerY = 35; // 上からの距離（%）- 上に移動
          const x = Math.cos(angleRad) * radius;
          const y = Math.sin(angleRad) * radius;

          return (
            <ServiceTab
              key={i}
              selected={isSelected}
              gradient={service.gradient}
              onClick={() => onSelect(i)}
              initial={false}
              animate={{
                scale: isSelected ? 1.15 : 1,
                opacity: isSelected ? 1 : 0.5,
              }}
              whileHover={{
                scale: isSelected ? 1.2 : 1.05,
                opacity: 1,
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
              }}
              style={{
                position: 'absolute',
                right: `${centerX - x}px`,
                top: `calc(${centerY}% + ${y}px)`,
              }}
            >
              {/* 選択時の光るリングエフェクト */}
              {isSelected && (
                <ServiceTabRing
                  initial={{ scale: 1, opacity: 0.8 }}
                  animate={{
                    scale: [1, 1.15, 1],
                    opacity: [0.5, 0.2, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              )}

              {/* グラスモーフィズム（曇りガラス）オーバーレイ */}
              <ServiceTabGlassOverlay selected={isSelected} />

              {/* アイコン画像 */}
              <ServiceIconContainer>
                <ServiceTabImage
                  src={service.tabIcon}
                  alt={service.title}
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: isSelected ? 1 : 0.7,
                    scale: isSelected ? 1 : 0.95,
                  }}
                  transition={{ duration: 0.3 }}
                  style={{ position: 'relative', zIndex: 10 }}
                />
              </ServiceIconContainer>
            </ServiceTab>
          );
        })}
      </ServiceTabs>
    </ServiceTabsWrapper>
  );
};

export const DesktopLayout = ({ currentService, services, selected, onSelect }) => {
  return (
    <ServicesContent>
      {/* Left: Device Mockup */}
      <DeviceMockupComponent currentService={currentService} />

      {/* Center: Service Description */}
      <CenterContent>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentService.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              style={{ position: 'relative', zIndex: 1 }}
            >
              <MainTitle>
                <span style={mainTitleStyle(currentService)}>{currentService.mainTitle || 'EC'}</span>
                {resolveWorkspaceText(currentService) && (
                  <WorkSpaceText>{resolveWorkspaceText(currentService)}</WorkSpaceText>
                )}
                {currentService.badge && (
                  <StatusBadge sx={badgeSx(currentService)}>{currentService.badge}</StatusBadge>
                )}
              </MainTitle>
              <Subtitle>{currentService.subtitle}</Subtitle>
              <Divider />
              <Description>{currentService.description}</Description>
              <ServiceLinkComponent service={currentService} />
            </motion.div>
          </AnimatePresence>
      </CenterContent>

      {/* Right: Service Tabs */}
      <ServiceTabsComponent
        services={services}
        selected={selected}
        onSelect={onSelect}
      />
    </ServicesContent>
  );
};

export const MobileLayout = ({ currentService, selected, services, handlePrev, handleNext, direction, slideVariants }) => {
  return (
    <Box
      sx={{
        display: { xs: 'block', lg: 'none' },
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Left Navigation Icon */}
      <LeftNavIcon
        src="/icon_left.png"
        alt="Previous"
        onClick={selected === 0 ? undefined : handlePrev}
        style={{ 
          opacity: selected === 0 ? 0.3 : 1,
          cursor: selected === 0 ? 'not-allowed' : 'pointer',
          pointerEvents: selected === 0 ? 'none' : 'auto'
        }}
      />

      {/* Right Navigation Icon */}
      <RightNavIcon
        src="/icon_right.png"
        alt="Next"
        onClick={selected === services.length - 1 ? undefined : handleNext}
        style={{ 
          opacity: selected === services.length - 1 ? 0.3 : 1,
          cursor: selected === services.length - 1 ? 'not-allowed' : 'pointer',
          pointerEvents: selected === services.length - 1 ? 'none' : 'auto'
        }}
      />

      <ServicesContent>
        <AnimatePresence mode="wait" custom={direction}>
          <ServiceCard
            key={selected}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {/* Monitor Image */}
            <DeviceMockupComponent currentService={currentService} />

            {/* Service Description */}
            <CenterContent>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <MainTitle>
                  <span style={mainTitleStyle(currentService)}>{currentService.mainTitle || 'EC'}</span>
                  {resolveWorkspaceText(currentService) && (
                    <WorkSpaceText>{resolveWorkspaceText(currentService)}</WorkSpaceText>
                  )}
                  {currentService.badge && (
                    <StatusBadge sx={badgeSx(currentService)}>{currentService.badge}</StatusBadge>
                  )}
                </MainTitle>
                <Subtitle>{currentService.subtitle}</Subtitle>
                <Divider />
                <Description>{currentService.description}</Description>
                <ServiceLinkComponent service={currentService} />
              </Box>
            </CenterContent>
          </ServiceCard>
        </AnimatePresence>
      </ServicesContent>
    </Box>
  );
};

